/**
 * Helpers for editing a merged association's target entity — mapping an
 * association to a searchable entity type, and converting the current target
 * into an EntityResult to pre-fill the picker. Kept separate from the
 * AssociationTargetEditor component so both it and MergedCreateEditForm can
 * import them (and to keep the component file component-only for fast refresh).
 */

import { resolveEntityName } from '@/lib/proposals/utils/entityName';
import type { EntityResult, EntityType } from '@/lib/search';
import { isEntityType } from '@/lib/search';

import type {
  ObjectCreatedEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isObjectCreatedPayload } from '../../lib/proposals/core/types';
import type { MergedAssociation } from '../../lib/proposals/utils/actionMerging';

// Entity types the search API (EntityTypeahead) can resolve, and that make
// sense as association targets to swap.
const SEARCHABLE_TARGET_TYPES = new Set<EntityType>([
  'ahoy_contact',
  'ahoy_company',
  'ahoy_deal',
  'ahoy_user',
]);

// association_type_name suffix → target entity type (fallback when enriched
// data doesn't carry the target's entity_type_name). Typed as Record<string, string>
// (not EntityType) to accommodate non-searchable types such as ahoy_risk.
export const ASSOCIATION_TARGET_TYPE_MAP: Record<string, string> = {
  _to_company: 'ahoy_company',
  _to_contact: 'ahoy_contact',
  _to_deal: 'ahoy_deal',
  _to_user: 'ahoy_user',
  // Owner associations (e.g. ahoy_contact_to_owner, ahoy_deal_to_owner) point at
  // an ahoy_user. Map the suffix so the target resolves to a searchable type when
  // enriched data doesn't carry the target's entity_type_name.
  _to_owner: 'ahoy_user',
  _to_risk: 'ahoy_risk',
};

export const TARGET_TYPE_NOUN: Partial<Record<EntityType, string>> = {
  ahoy_company: 'company',
  ahoy_contact: 'contact',
  ahoy_deal: 'deal',
  ahoy_user: 'user',
};

export const TARGET_TYPE_PLURAL: Partial<Record<EntityType, string>> = {
  ahoy_company: 'companies',
  ahoy_contact: 'contacts',
  ahoy_deal: 'deals',
  ahoy_user: 'users',
};

export function deriveTypeFromAssociation(
  associationType: string
): string | undefined {
  for (const [suffix, type] of Object.entries(ASSOCIATION_TARGET_TYPE_MAP)) {
    if (associationType.endsWith(suffix)) {
      return type;
    }
  }
  return undefined;
}

/**
 * The searchable target entity type for an association, or null when the target
 * type can't be resolved/searched (in which case the association stays
 * read-only).
 */
export function getSearchableTargetType(
  assoc: MergedAssociation
): EntityType | null {
  const raw =
    assoc.enrichedData?.target_object?.entity_type_name ??
    deriveTypeFromAssociation(assoc.associationType);
  if (raw && isEntityType(raw) && SEARCHABLE_TARGET_TYPES.has(raw)) {
    return raw;
  }
  return null;
}

/** Build a minimal EntityResult for a given type from an id + display name. */
export function makeEntityResult(
  type: EntityType,
  id: string,
  name: string
): EntityResult {
  const base = {
    id,
    entity_id: id,
    name,
    secondary_text: '',
    avatar_url: null,
  };
  switch (type) {
    case 'ahoy_company':
      return { ...base, type, domain: '' };
    case 'ahoy_contact':
      return { ...base, type, email: '' };
    case 'ahoy_user':
      return { ...base, type, email: '' };
    case 'ahoy_deal':
      return { ...base, type, amount: null, stage: null };
    default:
      // Callers only ever pass a type from SEARCHABLE_TARGET_TYPES (see
      // getSearchableTargetType); this default exists solely to satisfy the
      // exhaustive switch over the wider EntityType union.
      throw new Error(
        `Unsupported entity type for association target: ${type}`
      );
  }
}

/**
 * Whether an association's target can be edited via the entity picker. True for
 * any searchable target type — in-CRM OR being created in this same proposal.
 * The picker itself offers CRM search results plus the proposal's proposed
 * entities (see buildProposedEntityOptions), so a not-yet-created target is
 * re-pointable too. Non-searchable target types stay read-only.
 */
export function isSwappableAssociation(assoc: MergedAssociation): boolean {
  return getSearchableTargetType(assoc) != null;
}

/**
 * Whether an association targets a pipeline/lifecycle stage. Stage targets
 * aren't searchable entities — they're a bounded pick from the entity's
 * pipeline — so they get the stage dropdown (AssociatedPipelineStageInput)
 * rather than the entity typeahead. Kept here so both the read-only card and
 * the edit form classify stages identically.
 */
export function isStageAssociation(associationType: string): boolean {
  return (
    associationType.endsWith('_pipeline_stage') ||
    associationType.endsWith('_lifecycle_stage')
  );
}

/**
 * Build picker options for entities being CREATED elsewhere in the same
 * proposal (not yet in CRM) whose target type matches and whose create action
 * is currently selected. Deselecting a create action drops its entity here, so
 * it can no longer be picked while it won't be created.
 */
export function buildProposedEntityOptions(
  actions: ProposedAction[],
  isSelected: (actionId: number) => boolean,
  targetType: EntityType
): EntityResult[] {
  const options: EntityResult[] = [];
  for (const action of actions) {
    if (!isObjectCreatedPayload(action.payload)) {
      continue;
    }
    const payload = action.payload as ObjectCreatedEntityPayload;
    const entityType =
      action.enriched_data?.entity_type_name ?? payload.entity_type_name;
    if (entityType !== targetType || !isSelected(action.id)) {
      continue;
    }
    const label =
      resolveEntityName(payload.payload.properties, targetType) ||
      action.enriched_data?.object_label ||
      `New ${targetType.replace(/^ahoy_/, '')}`;
    options.push(makeEntityResult(targetType, payload.entity_id, label));
  }
  return options;
}

/**
 * The association's current target as an EntityResult (to pre-fill the picker),
 * or null when the target type isn't searchable.
 */
export function targetToEntityResult(
  assoc: MergedAssociation
): EntityResult | null {
  const type = getSearchableTargetType(assoc);
  if (!type) {
    return null;
  }
  const name = assoc.enrichedData?.target_object?.label ?? 'Associated object';
  return makeEntityResult(type, assoc.targetEntityId, name);
}
